import { useEffect, useState } from 'react';
import * as React from 'react';
import { useInView } from 'react-intersection-observer';

/*
  LazyLoad component is a wrapper for lazy loading components.
  Wrapped components are only rendered when they are in view.
  And because next/dynamic imported is used, and the import only
  happens when the component is rendered, then the import only happens
  when the component is in view.

  A fallback can also be provided to render while the component is not in view.
  Search engines like Google have sophisticated crawlers that can execute JS
  and render dynamic content. Not all search engines can do this, so
  this fallback can be useful for SSR, for SEO purposes.

  Usage:

  const YourComponent = dynamic(() => import('path/to/YourComponent'));

  <LazyLoad height='200px'>
    <YourComponent />
  </LazyLoad>
*/

interface LazyLoadProps {
  children: React.ReactNode;
  height: string;
  fallback?: React.ReactNode;
  id?: string;
  style?: React.CSSProperties;
}

const LazyLoad = (props: LazyLoadProps) => {
  const { children, height, id = null, fallback = null, style = {} } = props;
  const [loaded, setLoaded] = useState(false);
  const { ref, inView } = useInView({
    triggerOnce: true,
  });

  useEffect(() => {
    if (inView) {
      setLoaded(true);
    }
  }, [inView]);

  return (
    <div
      id={id}
      ref={ref}
      style={{
        minHeight: loaded ? height : '100vh',
        ...style,
      }}
    >
      {loaded ? children : fallback}
    </div>
  );
};

export default LazyLoad;
